import axios, {AxiosInstance} from "axios"
import {useAuthStore} from "../store/authStore"

/*
 * eu-central-1 API Gateway (aiwave-app-prod-api): /console/management, /console/firmware and
 * /devices/connection-status sit behind a Cognito JWT authorizer, so every call needs
 * "Authorization: Bearer <Cognito access token>". /console/authorize is public (login itself).
 */
export const getAuthHeaders = (): Record<string, string> => {
	const token = useAuthStore.getState().accessToken

	return token
		? {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			}
		: {
				"Content-Type": "application/json",
			}
}

// 401 from the JWT authorizer = missing, expired or foreign (e.g. old us-east-1) token -> back to login.
// Only when the rejected request carried the current token, so late 401s from requests sent
// before login/after logout (e.g. polling) don't wipe a fresh session or a pending OTP.
export const handleUnauthorized = (status?: number, sentAuthorization?: unknown) => {
	const {status: authStatus, accessToken, logout} = useAuthStore.getState()
	if (status === 401 && authStatus === "authenticated" && accessToken && sentAuthorization === `Bearer ${accessToken}`) {
		logout()
	}
}

// Adds the bearer token to every request and logs out on 401. Do NOT use for the auth (OTP) API:
// a wrong OTP code also returns 401 there.
export const withAuth = (instance: AxiosInstance): AxiosInstance => {
	instance.interceptors.request.use((config) => {
		const token = useAuthStore.getState().accessToken
		if (token) {
			config.headers.Authorization = `Bearer ${token}`
		}
		return config
	})

	instance.interceptors.response.use(
		(response) => response,
		(error) => {
			if (axios.isAxiosError(error)) {
				handleUnauthorized(error.response?.status, error.config?.headers?.Authorization)
			}
			return Promise.reject(error)
		},
	)

	return instance
}
